import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, BookOpen, Handshake, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! How can I assist you today with Iqra Skills? Whether you\'re interested in our courses, government programs, or partner opportunities, feel free to ask!',
      showQuickOptions: true
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const quickOptions = [
    { label: 'Courses', icon: BookOpen, query: 'Tell me about all courses offered by Iqra Skills with fees' },
    { label: 'Partner Opportunities', icon: Handshake, query: 'Tell me about partner/franchise opportunities and investment' },
    { label: 'Government Programs', icon: Building2, query: 'Tell me about government programs like PMKVY, NULM, NAPS' }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (messageText) => {
    const userMessage = messageText || input.trim();
    if (!userMessage || isLoading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chatbot`, {
        message: userMessage
      });

      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: response.data.response }
      ]);
    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'I apologize, but I am having trouble responding right now. Please try again or contact us at +91 9521484003.' }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickOption = (query) => {
    handleSend(query);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="h-14 w-14 rounded-full bg-blue-600 hover:bg-blue-700 shadow-lg"
          data-testid="chatbot-open-button"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      )}

      {isOpen && (
        <div className="bg-white rounded-lg shadow-2xl w-96 h-[520px] flex flex-col" data-testid="chatbot-window">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <MessageCircle className="h-5 w-5" />
              <span className="font-semibold">Iqra Skills Assistant</span>
            </div>
            <button onClick={() => setIsOpen(false)} data-testid="chatbot-close-button" className="hover:bg-blue-500 rounded p-1 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div key={index}>
                  <div
                    className={`flex ${
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                    data-testid={`chat-message-${message.role}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg p-3 ${
                        message.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-900'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                  
                  {/* Quick Options after welcome message */}
                  {message.showQuickOptions && index === 0 && !isLoading && messages.length === 1 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs text-slate-500 text-center mb-2">Select an option or type your question:</p>
                      <div className="flex flex-col gap-2">
                        {quickOptions.map((option, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleQuickOption(option.query)}
                            className="flex items-center gap-2 w-full px-3 py-2.5 bg-white border-2 border-blue-200 rounded-lg text-left hover:bg-blue-50 hover:border-blue-400 transition-all text-sm font-medium text-slate-700"
                            data-testid={`quick-option-${idx}`}
                          >
                            <option.icon className="h-4 w-4 text-blue-600" />
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 rounded-lg p-3">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-lg">
            <div className="flex space-x-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                disabled={isLoading}
                data-testid="chatbot-input"
                className="flex-1 bg-white"
              />
              <Button
                onClick={() => handleSend()}
                disabled={isLoading || !input.trim()}
                data-testid="chatbot-send-button"
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
